<script setup lang="ts">
  import { reactive, watch } from 'vue';
  import { useLoadingStore } from '@/stores/loading.ts';
  import { useAuthStore } from '@/stores/auth.ts';
  import { useApi } from '@/composables/use-api.ts';
  import {
    EmployeeSetDetailDto,
    EmployeeWithDetailDto,
  } from '@/services/api/employee-api.ts';

  const props = withDefaults(
    defineProps<{
      isVisible: boolean;
    }>(),
    { isVisible: false }
  );
  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const loading = useLoadingStore();
  const auth = useAuthStore();
  const { company } = useApi();
  const empSettings = reactive({
    isChatbotTaggingAlarm: false,
    isChatbotApprovalAlarm: false,
    isAttendanceMailAlarmYn: false,
    isMemberMailAlarmYn: false,
  });

  function reload(employeeWithDetails: EmployeeWithDetailDto) {
    empSettings.isChatbotTaggingAlarm =
      employeeWithDetails.chatbotTaggingAlarmYn == 'Y';
    empSettings.isChatbotApprovalAlarm =
      employeeWithDetails.chatbotApprovalAlarmYn == 'Y';
    empSettings.isAttendanceMailAlarmYn =
      employeeWithDetails.attendanceMailAlarmYn == 'Y';
    empSettings.isMemberMailAlarmYn =
      employeeWithDetails.memberMailAlarmYn == 'Y';
  }

  async function refreshAsync() {
    await loading.loadAsync(async () => {
      const employeeWithDetails = await company.employee.getSettingsAsync();
      reload(employeeWithDetails);
    });
  }

  async function saveAsync() {
    const request: EmployeeSetDetailDto = {
      chatbotTaggingAlarmYn: empSettings.isChatbotTaggingAlarm ? 'Y' : 'N',
      chatbotApprovalAlarmYn: empSettings.isChatbotApprovalAlarm ? 'Y' : 'N',
      attendanceMailAlarmYn: empSettings.isAttendanceMailAlarmYn ? 'Y' : 'N',
      memberMailAlarmYn: empSettings.isMemberMailAlarmYn ? 'Y' : 'N',
    };
    await loading.loadAsync(async () => {
      const employeeWithDetails =
        await company.employee.setSettingsAsync(request);
      reload(employeeWithDetails);
      emit('close');
    });
  }

  watch(
    () => props.isVisible,
    async (newValue) => {
      if (newValue) await refreshAsync();
    }
  );
</script>
<template>
  <AppModal
    id="settings"
    has-close-x
    is-bottom-on-mobile
    :is-visible="props.isVisible"
    @close="emit('close')">
    <h3 class="text-lg font-bold">설정</h3>
    <div class="py-4 px-2 mx-auto form-control w-30">
      <label class="cursor-pointer label">
        <!-- prettier-ignore -->
        <span class="label-text">
          출입 태깅 <b>챗봇</b> 알람
        </span>
        <input
          v-model="empSettings.isChatbotTaggingAlarm"
          :disabled="loading.isLoading"
          type="checkbox"
          class="toggle toggle-warning" />
      </label>
      <label class="cursor-pointer label">
        <!-- prettier-ignore -->
        <span class="label-text">
          결재 <b>챗봇</b> 알람
        </span>
        <input
          v-model="empSettings.isChatbotApprovalAlarm"
          :disabled="loading.isLoading"
          type="checkbox"
          class="toggle toggle-warning" />
      </label>
      <label class="cursor-pointer label">
        <!-- prettier-ignore -->
        <span class="label-text">
          근태 <b>메일</b> 알람
        </span>
        <input
          v-model="empSettings.isAttendanceMailAlarmYn"
          :disabled="loading.isLoading"
          type="checkbox"
          class="toggle toggle-warning" />
      </label>
      <label
        v-if="auth.isManager"
        class="cursor-pointer label">
        <!-- prettier-ignore -->
        <span class="label-text">
          조직원 근태 <b>메일</b> 알람
        </span>
        <input
          v-model="empSettings.isMemberMailAlarmYn"
          :disabled="loading.isLoading"
          type="checkbox"
          class="toggle toggle-warning" />
      </label>
    </div>
    <template #action>
      <div class="text-center">
        <button
          class="text-lg btn btn-block btn-warning"
          :disabled="loading.isLoading"
          @click="saveAsync">
          저장
        </button>
      </div>
    </template>
  </AppModal>
</template>
